import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy,
  Check,
  AlertTriangle,
  Timer,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Ban,
  Fingerprint,
  Network,
} from 'lucide-react';
import {
  mockVLLMInstanceDetails,
  mockTritonInstanceDetails,
  VLLMBotPrevention,
  TritonBotPrevention,
} from '@/mock/gpu-instance';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import { cn } from '@/lib/utils';

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const GPUInstanceBotPrevention = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { data: instances } = useListSSHInstances();

  const instance = instances?.find((i) => i.task_id === instanceId);
  const instanceTag = instance?.instance_tag;

  // Get bot prevention data based on instance type
  const { botPrevention, apiKey, modelName } = useMemo(() => {
    if (instanceTag === 'vllm') {
      const details = mockVLLMInstanceDetails(instanceId || '');
      return {
        botPrevention: details.botPrevention,
        apiKey: details.apiKey,
        modelName: details.modelName,
      };
    } else if (instanceTag === 'triton') {
      const details = mockTritonInstanceDetails(instanceId || '');
      return {
        botPrevention: details.botPrevention,
        apiKey: details.apiKey,
        modelName: 'bge-large-en-v1.5', // Default model for example
      };
    }
    // Default to vLLM if instance type not found
    const details = mockVLLMInstanceDetails(instanceId || '');
    return {
      botPrevention: details.botPrevention,
      apiKey: details.apiKey,
      modelName: details.modelName,
    };
  }, [instanceId, instanceTag]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Bot Prevention
          </h1>
          <p className="text-muted-foreground">
            Rate limiting and Proof of Work protection
          </p>
        </div>
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            botPrevention.enabled
              ? "text-green-600 border-green-600/30 bg-green-500/10"
              : "text-gray-500 border-gray-500/30"
          )}
        >
          {botPrevention.enabled ? (
            <><ShieldCheck className="h-3 w-3 mr-1" /> Active</>
          ) : (
            <><ShieldAlert className="h-3 w-3 mr-1" /> Disabled</>
          )}
        </Badge>
      </div>

      {/* Bot Prevention Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Protection Statistics</CardTitle>
          <CardDescription>24-hour protection metrics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Protection Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Ban className="h-3 w-3" />
                <span className="text-xs">Blocked (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.blockedRequests24h)}</div>
            </div>
            <div className="p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs">Rate Limited (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.rateLimitedRequests24h)}</div>
            </div>
            <div className="p-3 bg-green-500/5 rounded-lg border border-green-500/20">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Fingerprint className="h-3 w-3" />
                <span className="text-xs">PoW Solved (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.powChallengesSolved24h)}</div>
            </div>
            <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Timer className="h-3 w-3" />
                <span className="text-xs">Avg PoW Time</span>
              </div>
              <div className="text-lg font-bold">{botPrevention.stats.avgPowSolveTimeMs}ms</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Rate Limiting */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Rate Limiting by IP</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    botPrevention.rateLimit.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {botPrevention.rateLimit.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Minute</span>
                  <span className="font-mono">{botPrevention.rateLimit.requestsPerMinute}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Hour</span>
                  <span className="font-mono">{botPrevention.rateLimit.requestsPerHour}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Burst Limit</span>
                  <span className="font-mono">{botPrevention.rateLimit.burstLimit}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Blocked IPs</span>
                  <span className="font-mono">{botPrevention.rateLimit.blockedIPs.length}</span>
                </div>
              </div>
              {botPrevention.rateLimit.whitelistedIPs.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Whitelisted:</span>{' '}
                  {botPrevention.rateLimit.whitelistedIPs.join(', ')}
                </div>
              )}
            </div>

            {/* PoW Stats */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Proof of Work Stats</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    botPrevention.proofOfWork.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {botPrevention.proofOfWork.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Solved (24h)</span>
                  <span className="font-mono">{formatNumber(botPrevention.stats.powChallengesSolved24h)}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Avg Solve Time</span>
                  <span className="font-mono">{botPrevention.stats.avgPowSolveTimeMs}ms</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                See PoW Protected Endpoint section below for integration details.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PoW Protected Endpoint */}
      {botPrevention.proofOfWork.enabled && (
        <Card className="border-purple-500/30">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Fingerprint className="h-5 w-5 text-purple-500" />
                  PoW Protected Endpoint
                </CardTitle>
                <CardDescription>
                  Requires solving a SHA256 challenge before making inference requests
                </CardDescription>
              </div>
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                Challenge Required
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* PoW Configuration */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Algorithm</div>
                <div className="font-mono text-sm uppercase">{botPrevention.proofOfWork.algorithm}</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Difficulty</div>
                <Badge variant="secondary" className="text-xs capitalize">
                  {botPrevention.proofOfWork.difficulty}
                </Badge>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Challenge Validity</div>
                <div className="font-mono text-sm">{botPrevention.proofOfWork.validitySeconds}s</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Skip Auth Users</div>
                <div className="font-mono text-sm">{botPrevention.proofOfWork.exemptAuthenticatedUsers ? 'Yes' : 'No'}</div>
              </div>
            </div>

            {/* Challenge Flow */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Integration Flow</div>

              {/* Step 1 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-blue-600 text-white text-xs font-semibold">Step 1</span>
                  <span className="text-sm font-medium">Request Challenge</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`GET ${botPrevention.proofOfWork.challengeEndpoint}

Response:
{
  "challenge": "a8f3e2...",
  "difficulty": 4,
  "expires_at": 1704067200
}`}
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-1 right-1 h-7 text-gray-400 hover:text-white"
                    onClick={() => handleCopy(`GET ${botPrevention.proofOfWork.challengeEndpoint}`, 'pow-step1')}
                  >
                    {copiedField === 'pow-step1' ? <Check className="size-3" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-amber-500 text-white text-xs font-semibold">Step 2</span>
                  <span className="text-sm font-medium">Solve Challenge (Client-side)</span>
                </div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`// Find nonce where SHA256(challenge + nonce) starts with N zeros
let nonce = 0;
while (true) {
  const hash = SHA256(challenge + nonce);
  if (hash.startsWith("0".repeat(difficulty))) break;
  nonce++;
}`}
                </pre>
              </div>

              {/* Step 3 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-emerald-600 text-white text-xs font-semibold">Step 3</span>
                  <span className="text-sm font-medium">Make Inference Request with Solution</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{instanceTag === 'triton' ? `POST ${botPrevention.proofOfWork.protectedEndpoint.replace('{MODEL_NAME}', modelName)}
Headers:
  X-PoW-Challenge: <challenge>
  X-PoW-Nonce: <nonce>
  Authorization: Bearer ${apiKey}

Body:
{
  "inputs": [{"name": "text", "data": ["Hello"], "datatype": "BYTES"}]
}` : `POST ${botPrevention.proofOfWork.protectedEndpoint}
Headers:
  X-PoW-Challenge: <challenge>
  X-PoW-Nonce: <nonce>
  Authorization: Bearer ${apiKey}

Body:
{
  "model": "${modelName}",
  "messages": [{"role": "user", "content": "Hello!"}],
  "stream": true
}`}
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-1 right-1 h-7 text-gray-400 hover:text-white"
                    onClick={() => handleCopy('pow-step3-code', 'pow-step3')}
                  >
                    {copiedField === 'pow-step3' ? <Check className="size-3" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Endpoints Summary */}
            <div className="pt-4 border-t space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Endpoints</div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">GET</Badge>
                    <span className="text-muted-foreground">Challenge</span>
                  </div>
                  <code className="font-mono text-xs">{botPrevention.proofOfWork.challengeEndpoint}</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">POST</Badge>
                    <span className="text-muted-foreground">Protected API</span>
                  </div>
                  <code className="font-mono text-xs">{botPrevention.proofOfWork.protectedEndpoint}</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GPUInstanceBotPrevention;
